import { DOCUMENT_VIEWER_CONFIG, SECURITY_MESSAGES } from '../utils/constants';
import documentViewerAPI from './documentViewerService';

/**
 * Manages viewing session heartbeat and expiry
 */
class SessionManager {
  constructor() {
    this.heartbeatInterval = null;
    this.warningTimeout = null;
    this.sessionToken = null;
    this.expiresAt = null;
    this.onExpiry = null;
    this.onWarning = null;
  }

  /**
   * Start session management
   */
  start(sessionToken, expiresAt, onExpiry, onWarning) {
    this.sessionToken = sessionToken;
    this.expiresAt = new Date(expiresAt);
    this.onExpiry = onExpiry;
    this.onWarning = onWarning;
    
    // Start heartbeat
    this.startHeartbeat();
    
    // Set warning timer
    this.setWarningTimer();
    
    console.log('📡 Session manager started');
  }

  /**
   * Stop session management
   */
  stop() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    
    console.log('📡 Session manager stopped');
  }

  /**
   * Send periodic heartbeat to keep session alive
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(async () => {
      try {
        const response = await documentViewerAPI.sendHeartbeat(this.sessionToken);
        console.log('💓 Heartbeat sent, time remaining:', response.time_remaining, 'seconds');
        
        // Check if session is about to expire
        if (response.time_remaining < 60) {
          console.warn('⚠️ Session expiring soon!');
        }
      } catch (error) {
        console.error('Heartbeat failed:', error);
        
        // If heartbeat fails, session might be expired
        if (error.response?.status === 401) {
          this.handleExpiry();
        }
      }
    }, DOCUMENT_VIEWER_CONFIG.HEARTBEAT_INTERVAL);
  }

  /**
   * Set timer to warn user before expiry
   */
  setWarningTimer() {
    const timeUntilWarning = this.expiresAt.getTime() - Date.now() - DOCUMENT_VIEWER_CONFIG.SESSION_WARNING_TIME;
    
    if (timeUntilWarning > 0) {
      this.warningTimeout = setTimeout(() => {
        console.warn('⏰ Session expiring in 5 minutes');
        if (this.onWarning) {
          this.onWarning();
        }
      }, timeUntilWarning);
    }
  }

  /**
   * Handle session expiry
   */
  handleExpiry() {
    this.stop();
    console.error('❌ Session expired');
    
    if (this.onExpiry) {
      this.onExpiry();
    }
  }

  /**
   * Get time remaining in session
   */
  getTimeRemaining() {
    const now = Date.now();
    const remaining = this.expiresAt.getTime() - now;
    return Math.max(0, Math.floor(remaining / 1000)); // seconds
  }
}

export default SessionManager;